/**
 * @flow
 */

import type { FormState } from './types'
import type { Action } from '../../app/types'

const ensureSchoolIds = (schools = []) => {
  return schools.map((school) => {
    if (!school.id) {
      return { ...school, id: crypto.randomUUID() }
    }
    return school
  })
}

const ensureJobIds = (jobs = []) => {
  return jobs.map((job) => {
    if (!job.id) {
      return { ...job, id: crypto.randomUUID() }
    }
    return job
  })
}

const ensureSkillIds = (skills = []) => {
  return skills.map((skill) => {
    if (!skill.id) {
      return { ...skill, id: crypto.randomUUID() }
    }
    return skill
  })
}

const ensureProjectIds = (projects = []) => {
  return projects.map((project) => {
    if (!project.id) {
      return { ...project, id: crypto.randomUUID() }
    }
    return project
  })
}

const ensureAwardIds = (awards = []) => {
  return awards.map((award) => {
    if (!award.id) {
      return { ...award, id: crypto.randomUUID() }
    }
    return award
  })
}

export const initialState = {
  resume: {
    values: {
      selectedTemplate: 1,
      headings: {
        work: 'Work Experience',
        education: 'Education',
        skills: 'Skills',
        projects: 'Projects',
        awards: 'Awards',
      },
      basics: {},
      education: [],
      work: [],
      skills: [],
      projects: [],
      awards: [],
    },
  },
  jsonUpload: {
    status: null,
    errMessage: null,
  },
}

function form(state = initialState, action) {
  switch (action.type) {
    case 'UPLOAD_JSON_REQUEST':
      return {
        ...state,
        jsonUpload: {
          status: 'pending',
          errMessage: null,
        },
      }

    case 'UPLOAD_JSON_SUCCESS':
      return {
        ...state,
        resume: {
          ...state.resume,
          values: action.json,
        },
        jsonUpload: {
          status: 'success',
          errMessage: null,
        },
      }

    case 'UPLOAD_JSON_FAILURE':
      return {
        ...state,
        jsonUpload: {
          status: 'failure',
          errMessage: action.errMessage,
        },
      }

    case 'SELECT_TEMPLATE':
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            selectedTemplate: action.templateId,
            basics: {
              ...state.resume.values.basics,
              summaries:
                action.templateId === 10
                  ? ['']
                  : state.resume.values.basics?.summaries || [],
            },
          },
        },
      }

    case 'UPDATE_FORM_VALUES':
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            ...action.values,
            basics: {
              ...state.resume.values.basics,
              ...action.values.basics,
              summaries:
                action.values.basics?.summaries ||
                state.resume.values.basics?.summaries ||
                [],
            },
          },
        },
      }

    case 'ADD_PROFILE_SUMMARY': {
      const summaries = state.resume.values.basics?.summaries || []
      const newSummaries = [
        ...summaries.slice(0, action.index + 1),
        '',
        ...summaries.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            basics: {
              ...state.resume.values.basics,
              summaries: newSummaries,
            },
          },
        },
      }
    }

    case 'REMOVE_PROFILE_SUMMARY': {
      const summaries = state.resume.values.basics?.summaries || []
      const newSummaries = [
        ...summaries.slice(0, action.index),
        ...summaries.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            basics: {
              ...state.resume.values.basics,
              summaries: newSummaries,
            },
          },
        },
      }
    }

    case 'ADD_SCHOOL': {
      const education = state.resume.values.education || []
      const newEducation = [
        ...education.slice(0, action.index + 1),
        { id: crypto.randomUUID() },
        ...education.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            education: newEducation,
          },
        },
      }
    }

    case 'REMOVE_SCHOOL': {
      const education = state.resume.values.education || []
      const newEducation = [
        ...education.slice(0, action.index),
        ...education.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            education: newEducation,
          },
        },
      }
    }

    case 'REORDER_SCHOOLS': {
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            education: ensureSchoolIds(action.newOrder),
          },
        },
      }
    }

    case 'ADD_JOB': {
      const work = state.resume.values.work || []
      const newWork = [
        ...work.slice(0, action.index + 1),
        { id: crypto.randomUUID(), highlights: [] },
        ...work.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            work: newWork,
          },
        },
      }
    }

    case 'REMOVE_JOB': {
      const work = state.resume.values.work || []
      const newWork = [
        ...work.slice(0, action.index),
        ...work.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            work: newWork,
          },
        },
      }
    }

    case 'ADD_JOB_HIGHLIGHT': {
      const work = state.resume.values.work || []
      const job = work[action.jobIndex]
      const highlights = job.highlights || []
      const newHighlights = [...highlights, '']
      const newWork = [
        ...work.slice(0, action.jobIndex),
        { ...job, highlights: newHighlights },
        ...work.slice(action.jobIndex + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            work: newWork,
          },
        },
      }
    }

    case 'REMOVE_JOB_HIGHLIGHT': {
      const work = state.resume.values.work || []
      const job = work[action.jobIndex]
      const highlights = job.highlights || []
      const newHighlights = [
        ...highlights.slice(0, action.highlightIndex),
        ...highlights.slice(action.highlightIndex + 1),
      ]
      const newWork = [
        ...work.slice(0, action.jobIndex),
        { ...job, highlights: newHighlights },
        ...work.slice(action.jobIndex + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            work: newWork,
          },
        },
      }
    }

    case 'ADD_SKILL': {
      const skills = state.resume.values.skills || []
      const newSkills = [
        ...skills.slice(0, action.index + 1),
        { id: crypto.randomUUID(), keywords: [] },
        ...skills.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            skills: newSkills,
          },
        },
      }
    }

    case 'REMOVE_SKILL': {
      const skills = state.resume.values.skills || []
      const newSkills = [
        ...skills.slice(0, action.index),
        ...skills.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            skills: newSkills,
          },
        },
      }
    }

    case 'ADD_SKILL_KEYWORD': {
      const skills = state.resume.values.skills || []
      const skill = skills[action.skillIndex]
      const keywords = skill.keywords || []
      const newKeywords = [
        ...keywords.slice(0, action.keywordIndex),
        '',
        ...keywords.slice(action.keywordIndex),
      ]
      const newSkills = [
        ...skills.slice(0, action.skillIndex),
        { ...skill, keywords: newKeywords },
        ...skills.slice(action.skillIndex + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            skills: newSkills,
          },
        },
      }
    }

    case 'REMOVE_SKILL_KEYWORD': {
      const skills = state.resume.values.skills || []
      const skill = skills[action.skillIndex]
      const keywords = skill.keywords || []
      const newKeywords = [
        ...keywords.slice(0, action.keywordIndex),
        ...keywords.slice(action.keywordIndex + 1),
      ]
      const newSkills = [
        ...skills.slice(0, action.skillIndex),
        { ...skill, keywords: newKeywords },
        ...skills.slice(action.skillIndex + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            skills: newSkills,
          },
        },
      }
    }

    case 'ADD_PROJECT': {
      const projects = state.resume.values.projects || []
      const newProjects = [
        ...projects.slice(0, action.index + 1),
        { id: crypto.randomUUID(), keywords: [] },
        ...projects.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            projects: newProjects,
          },
        },
      }
    }

    case 'REMOVE_PROJECT': {
      const projects = state.resume.values.projects || []
      const newProjects = [
        ...projects.slice(0, action.index),
        ...projects.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            projects: newProjects,
          },
        },
      }
    }

    case 'ADD_PROJECT_KEYWORD': {
      const projects = state.resume?.values.projects || []
      if (!projects[action.projectIndex]) {
        return state
      }

      const project = projects[action.projectIndex]
      const keywords = project.keywords || []
      const newKeywords = [
        ...keywords.slice(0, action.keywordIndex),
        '',
        ...keywords.slice(action.keywordIndex),
      ]
      const newProjects = [
        ...projects.slice(0, action.projectIndex),
        { ...project, keywords: newKeywords },
        ...projects.slice(action.projectIndex + 1),
      ]

      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume?.values,
            projects: newProjects,
          },
        },
      }
    }

    case 'REMOVE_PROJECT_KEYWORD': {
      const projects = state.resume?.values.projects || []
      if (!projects[action.projectIndex]) {
        return state
      }

      const project = projects[action.projectIndex]
      const keywords = project.keywords || []
      if (keywords.length <= 1) {
        return state
      }

      const newKeywords = [
        ...keywords.slice(0, action.keywordIndex),
        ...keywords.slice(action.keywordIndex + 1),
      ]
      const newProjects = [
        ...projects.slice(0, action.projectIndex),
        { ...project, keywords: newKeywords },
        ...projects.slice(action.projectIndex + 1),
      ]

      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume?.values,
            projects: newProjects,
          },
        },
      }
    }

    case 'ADD_AWARD': {
      const awards = state.resume.values.awards || []
      const newAwards = [
        ...awards.slice(0, action.index + 1),
        { id: crypto.randomUUID() },
        ...awards.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            awards: newAwards,
          },
        },
      }
    }

    case 'REMOVE_AWARD': {
      const awards = state.resume.values.awards || []
      const newAwards = [
        ...awards.slice(0, action.index),
        ...awards.slice(action.index + 1),
      ]
      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            awards: newAwards,
          },
        },
      }
    }

    case 'REORDER_JOBS':
      if (!state.resume) {
        return state
      }

      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            work: ensureJobIds(action.newOrder),
          },
        },
      }

    case 'REORDER_SKILLS':
      if (!state.resume) {
        return state
      }

      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            skills: ensureSkillIds(action.newOrder),
          },
        },
      }

    case 'REORDER_PROJECTS':
      if (!state.resume) {
        return state
      }

      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            projects: ensureProjectIds(action.newOrder),
          },
        },
      }

    case 'REORDER_AWARDS':
      if (!state.resume) {
        return state
      }

      return {
        ...state,
        resume: {
          ...state.resume,
          values: {
            ...state.resume.values,
            awards: ensureAwardIds(action.newOrder),
          },
        },
      }

    default:
      return state
  }
}

export default form
